import i18n from "i18next";
import { DateTime } from "luxon";
import React, { Dispatch, SetStateAction } from "react";
import { AppDispatch } from "../../../app/store";
import { UserOrEmailMapping } from "../../../features/Admin/typings/admin.types";
import { setInputs } from "../../../features/Booking-Form/slices/booking.slice";
import {
  BookingType,
  Frequence,
  NotSerializedBookingInputs,
  NotSerializedBookingMode
} from "../../../features/Booking-Form/typings/booking-inputs";
import { getAllUsers } from "../../../utils/axios-requests";
import { checkEndBeforeStart, checkStartInPast } from "../../../utils/utilities";
import { getDateValueType } from "../DateTimePicker/functions/getDateValueType.function";

export const setDatesAndValidate = (
  inputs: NotSerializedBookingInputs,
  setMessage: Dispatch<SetStateAction<string | undefined>>,
  setValidDates: React.Dispatch<SetStateAction<boolean>>,
  maxDate?: string | null
) => {
  // the error must be instanced here to grant proper translation
  const errorMessages = {
    endBeforeStart: i18n.t("_endBeforeStart"),
    startInPast: i18n.t("_startInPast"),
    missing: i18n.t("_missing"),
    noWeekdays: i18n.t("_noWeekdays"),
    duration: i18n.t("_tooLongBooking"),
    selectedSpecific: i18n.t("_selectSpecificDates"),
    endAfterMaxTime: i18n.t("_endAfterMaxTime")
  };

  const invalidate = (message: string) => {
    setValidDates(false);
    setMessage(message);
  };

  const { bookingStart, bookingEnd, bookingFrom, bookingTo, weekdays, frequence, specificDays } =
    inputs;

  // Missing required values
  if (!bookingStart || !bookingEnd || !bookingFrom || !bookingTo) {
    invalidate(errorMessages.missing);
    return;
  }

  // Start time is in the past
  if (checkStartInPast(bookingFrom, bookingStart)) {
    invalidate(errorMessages.startInPast);
    return;
  }

  // End time is before start time
  if (checkEndBeforeStart(bookingFrom, bookingStart, bookingTo, bookingEnd)) {
    invalidate(errorMessages.endBeforeStart);
    return;
  }

  // Schedule booking but no weekdays selected
  const isSchedule =
    DateTime.fromISO(bookingFrom).valueOf() !== DateTime.fromISO(bookingTo).valueOf();

  if (isSchedule && (!weekdays || !Object.values(weekdays).includes(true))) {
    invalidate(errorMessages.noWeekdays);
    return;
  }

  // SPECIFIC mode but no dates selected
  if (frequence === Frequence.SPECIFIC && (!specificDays || specificDays.length === 0)) {
    invalidate(errorMessages.selectedSpecific);
    return;
  }

  // End time after max time when max date has a time set
  if (maxDate && getDateValueType(maxDate, bookingTo)) {
    const maxTime = DateTime.fromISO(maxDate).toFormat("HH:mm");
    if (bookingEnd > maxTime) {
      invalidate(errorMessages.endAfterMaxTime);
      return;
    }
  }

  // All checks passed
  setValidDates(true);
};

export const handleConference = (
  inputs: NotSerializedBookingInputs,
  dispatch: AppDispatch,
  users: UserOrEmailMapping[],
  setUsers: Dispatch<SetStateAction<UserOrEmailMapping[]>>,
  sub: string
) => {
  if (inputs.mode === "conferencezone" && inputs.activityBasedBooking) {
    dispatch(setInputs({ zoneAccess: [] }));
    if (users.length === 0)
      getAllUsers().then(r =>
        setUsers(r.data.filter((user: { userId: string }) => user.userId !== sub))
      );
  }
};

export const handleSetInputs = (
  inputs: NotSerializedBookingInputs,
  dispatch: AppDispatch,
  standardEndTime: string | undefined
) => {
  if (inputs.bookingFrom !== inputs.bookingTo) {
    dispatch(
      setInputs({
        bookingStart: DateTime.now().plus({ minutes: 30 }).toFormat("HH:mm"),
        bookingEnd:
          standardEndTime ??
          DateTime.now().set({ hour: 17, minute: 0, second: 0 }).toFormat("HH:mm"),
        bookingFrom: DateTime.now().toFormat("yyyy-MM-dd"),
        bookingTo: DateTime.now().toFormat("yyyy-MM-dd")
      })
    );
  }
};

/**
 * Always round up start time to the next quarter (e.g. current time = 10:03 am -> start time 10:15 am
 * if users default end time is in the past (so would be start time) make end time = start time + 1h
 * if start time + 1h is tomorrow make end time 23:59
 * The red warning message that start should not be in the past should never appear as default
 */
export const initBookingTime = (
  bookingStart: string,
  bookingEnd: string,
  mode?: NotSerializedBookingMode
): [string, string] => {
  // constant
  const defaultTimeFormat = "HH:mm";
  const localTime = DateTime.local();
  const min = calMinuteInterval(localTime);

  // local time with interval
  const localStart = localTime.set({ minute: min }).toFormat(defaultTimeFormat);
  const localEnd = localTime.plus({ hour: 1 }).set({ minute: min }).toFormat(defaultTimeFormat);

  const dateStart = DateTime.fromISO(bookingStart);
  const dateEnd = DateTime.fromISO(bookingEnd);
  const startWithInterval = dateStart
    .set({ minute: calMinuteInterval(dateStart) })
    .toFormat(defaultTimeFormat);
  const endWithInterval = dateEnd
    .set({ minute: calMinuteInterval(dateEnd) })
    .toFormat(defaultTimeFormat);

  // when conference room booking, set the initial duration to 60 mins
  if (mode === BookingType.CONFERENCEZONE) {
    const conferenceEnd = localTime
      .set({ minute: min })
      .plus({ hour: 1 })
      .toFormat(defaultTimeFormat);

    return [localStart, conferenceEnd];
  }

  // bookingStart <= now < bookingEnd, start only is in the past
  if (
    DateTime.fromISO(localTime.toFormat("HH:mm")) >= DateTime.fromISO(bookingStart) &&
    DateTime.fromISO(localTime.toFormat("HH:mm")) < DateTime.fromISO(bookingEnd)
  ) {
    if (localStart === endWithInterval) return [localStart, localEnd];

    return [localStart, endWithInterval];
  }

  // bookingStart < now && bookingEnd < now, both are in the past
  if (
    DateTime.fromISO(localTime.toFormat("HH:mm")) > DateTime.fromISO(bookingStart) &&
    DateTime.fromISO(localTime.toFormat("HH:mm")) > DateTime.fromISO(bookingEnd)
  ) {
    const setDay = DateTime.fromISO(localTime.toFormat("HH:mm")).plus({ hour: 1 }).day;
    const today = DateTime.fromISO(localTime.toFormat("HH:mm")).day;

    // but the case of start + 1 hour is tomorrow
    if (setDay > today) {
      return [localStart, "23:59"];
    }
    return [localStart, localEnd];
  }

  return [startWithInterval, endWithInterval];
};

/**
 * Default time range for a resource-book row for the given calendar start date.
 * - **Start date is today** (tenant timezone): same as {@link initBookingTime} / FormDateTime.
 * - **Other dates**: standard start–end; conference uses a one-hour slot from standard start.
 */
export function chosenTimesForResourceBookCalendarDay(opts: {
  bookingFromIso: string;
  bookingType: BookingType | undefined;
  standardEndTime: string;
  standardStartTime: string;
  timezone: string;
}): { endTime: string; startTime: string } {
  const today = DateTime.now().setZone(opts.timezone).toISODate() ?? "";
  if (opts.bookingFromIso === today) {
    const [startTime, endTime] = initBookingTime(
      opts.standardStartTime,
      opts.standardEndTime,
      opts.bookingType ?? undefined
    );
    return { startTime, endTime };
  }

  if (opts.bookingType === BookingType.CONFERENCEZONE) {
    const start = opts.standardStartTime;
    const startDt = DateTime.fromISO(`${opts.bookingFromIso}T${start}`, { zone: opts.timezone });
    if (startDt.isValid) {
      return {
        startTime: start,
        endTime: startDt.plus({ hours: 1 }).toFormat("HH:mm")
      };
    }
  }

  return {
    startTime: opts.standardStartTime,
    endTime: opts.standardEndTime
  };
}

/**
 * Resource-book dialog: uses `chosenTimesForResourceBookCalendarDay`, unless both persisted
 * times exist in Redux **and** `persistedBookingFrom` matches `bookingFromIso` (same day).
 */
export function initialChosenTimesForResourceBookDialog(opts: {
  bookingFromIso: string;
  bookingType: BookingType | undefined;
  persistedBookingEnd?: string | null;
  persistedBookingFrom?: string | null;
  persistedBookingStart?: string | null;
  standardEndTime: string;
  standardStartTime: string;
  timezone: string;
}): { endTime: string; startTime: string } {
  const startTrim = opts.persistedBookingStart?.trim();
  const endTrim = opts.persistedBookingEnd?.trim();
  const persistedFrom = opts.persistedBookingFrom?.trim();
  const fromMatches = Boolean(persistedFrom) && persistedFrom === opts.bookingFromIso;

  if (startTrim && endTrim && fromMatches) {
    return { startTime: startTrim, endTime: endTrim };
  }

  return chosenTimesForResourceBookCalendarDay({
    bookingFromIso: opts.bookingFromIso,
    bookingType: opts.bookingType,
    standardEndTime: opts.standardEndTime,
    standardStartTime: opts.standardStartTime,
    timezone: opts.timezone
  });
}

// return minutes based on 15 minutes interval
export const calMinuteInterval = (time: DateTime) => Math.ceil(time.minute / 15) * 15;

// return valid time range for editing schedule time
export function validTimeRange(startDate: string) {
  const startDateWOTime = DateTime.fromISO(startDate.slice(0, 10));
  const localTimeInterval = DateTime.local()
    .set({ minute: calMinuteInterval(DateTime.local()) })
    .toFormat("HH:mm");

  return startDateWOTime.diffNow().valueOf() > 0 ? "00:00" : localTimeInterval;
}

/**
 * fleet-booking specified
 * include all day boolean for daily bookings in case every 1 day is set (if every 2 days this logic does not apply anymore
 * in case daily booking and interval is every 1 day
 * booking uses the date and time differently
 * date: 05. 05 - 09. 05 / time: 08:00, 17:00
 * -> pick up of vehicle is 05.05 8 am and hand over is 05.09 5 pm
 * @TODO nececessary??? because onetime will handle start date and end date, too
 */
export function handleVehicleDailyBooking({
  mode,
  frequence,
  interval,
  dispatch
}: {
  mode: NotSerializedBookingMode;
  frequence: Frequence | undefined;
  interval: number | undefined;
  dispatch: AppDispatch;
}) {
  if (mode === BookingType.VEHICLE && frequence === Frequence.DAILY && interval === 1) {
    // update allDay value
    dispatch(setInputs({ allDay: true }));
  }
}
